/**
 * Plane property getters and registration - NON-HOOK VERSION FOR POTREE
 *
 * This file contains only framework-agnostic code (no React hooks, no Apollo).
 * It can be safely imported by potree_fork without pulling in React dependencies.
 */

import { AnnotationType, PropertyType, PropertyValueType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { getGlobalPropertyService } from '../../reactive/globalPropertyService';

// ============================================================================
// PROPERTY REGISTRATION
// ============================================================================

/**
 * Register plane-specific properties in the registry.
 * Call this once at app initialization.
 */
export function registerPlaneProperties() {
  if (!propertyRegistry.isRegistered('plane_tolerance')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: 'plane_tolerance',
      name: 'tolerance',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0.01,
      description: 'Distance tolerance for the plane clip area',
      ui: {
        group: 'filters',
        order: 2,
        tooltip: 'Set the tolerance distance for the plane filter',
      },
      validation: { min: 0.01, step: 0.01 },
      // Read by virtual PointoramaPlane clip helpers (map/side/line/floorplan 2D);
      // not shown in any property panel, so no annotation type gates it.
      annotationTypes: [AnnotationType.SURFACE],
    });
  }

  if (!propertyRegistry.isRegistered('heatmap_gradient')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: 'heatmap_gradient',
      name: 'gradient',
      valueType: PropertyValueType.GRADIENT,
      defaultValue: 'SPECTRAL',
      description: 'Color gradient for heatmap visualization',
      ui: {
        group: 'filters',
        order: 3,
        tooltip: 'Select the color gradient used for heatmap visualization',
        useGradientStyling: true,
        visibilityConditions: [{ propertyIdentifier: 'clip_method.{objectIdentifier}', value: 'HEATMAP' }],
      },
      annotationTypes: [AnnotationType.SURFACE],
    });
  }

  if (!propertyRegistry.isRegistered('heatmap_isolate')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: 'heatmap_isolate',
      name: 'isolate',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: false,
      description: 'Enable isolate mode for heatmap visualization',
      ui: {
        group: 'filters',
        order: 4,
        tooltip: 'When enabled, isolates the heatmap visualization',
        visibilityConditions: [{ propertyIdentifier: 'clip_method.{objectIdentifier}', value: 'HEATMAP' }],
      },
      annotationTypes: [AnnotationType.SURFACE],
    });
  }

  if (!propertyRegistry.isRegistered('heatmap_legend')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: 'heatmap_legend',
      name: 'legend',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: true,
      description: 'Enable legend for heatmap visualization',
      ui: {
        group: 'filters',
        order: 5,
        tooltip: 'When enabled, shows a legend for the heatmap visualization',
        visibilityConditions: [{ propertyIdentifier: 'clip_method.{objectIdentifier}', value: 'HEATMAP' }],
      },
      annotationTypes: [AnnotationType.SURFACE],
    });
  }
}

// Register properties immediately when module is imported
registerPlaneProperties();

// ============================================================================
// NON-HOOK HELPERS FOR POTREE (uses global property service)
// ============================================================================

/** Get reactive property for plane tolerance */
export function getPlaneToleranceProperty(planeId: string): ReactiveProperty<number> {
  const service = getGlobalPropertyService();
  return service.getProperty('plane_tolerance', planeId, PropertyType.PROJECT_USER) as ReactiveProperty<number>;
}

/** Get reactive property for heatmap gradient (Plane annotations) */
export function getPlaneHeatmapGradientProperty(planeId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty('heatmap_gradient', planeId, PropertyType.PROJECT_USER) as ReactiveProperty<string>;
}

/** Get reactive property for heatmap isolate mode (Plane annotations) */
export function getPlaneHeatmapIsolateProperty(planeId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('heatmap_isolate', planeId, PropertyType.PROJECT_USER) as ReactiveProperty<boolean>;
}

/** Get reactive property for heatmap legend visibility (Plane annotations) */
export function getPlaneHeatmapLegendProperty(planeId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('heatmap_legend', planeId, PropertyType.PROJECT_USER) as ReactiveProperty<boolean>;
}
